import type { SupabaseClient } from '@supabase/supabase-js';
import { createGrid, type ColDef, type GridApi, type GridOptions, type ICellRendererParams } from 'ag-grid-community';
import { showToast } from '../../lib/toast.js';
import { awaitJob } from '../../lib/await-job.js';
import { awaitOrderOutcome, latestActivityId } from '../../lib/await-order-outcome.js';
import { varioDocumentUrl, varioArticleUrl } from '../../lib/vario-links.js';
import { openModal, openErrorModal } from '@vario-connect/frontend-design';
import { infoBadge } from '../../lib/info-badge.js';
import { openVarioAppManagementTab } from '../../vario-iframe.js';
import { SETTINGS_SECTION_HANDOVER } from '../../lib/vario-route.js';
import {
  shouldAutoRefreshOrders,
  ordersRefreshSummary,
  ORDERS_AUTO_REFRESH_KEY,
} from '../../lib/orders-auto-refresh.js';

// Gambio-Bestellungen im NEUEN „grünen" Design (Outfit/ag-theme-vario), alle Klassen unter der
// Scope-Wurzel `.go-orders` (additives Stylesheet @vario-connect/frontend-design/gambio-orders.css
// - kollidiert nicht mit den globalen kl-*/v-*-Regeln). Inhalt = reiner Content-Renderer: Filter-
// leiste + Master/Detail-Split. Die App-Chrome (Header bzw. Tab-Leiste) liefert der Container:
// das Dashboard die kl-Tabs, das `/#/orders`-Verkaufskanal-Fenster den Mock-Header OHNE Menü.
// Funktional unverändert ggü. der kl-Version: Shop-/Status-Filter, Suche, „Aktualisieren" (Job-
// Fanout), Übernehmen/Erneut/Beheben, Fehler-Pille+Modal, Positionen + Vario-Deeplinks, Sprach-Badge.

interface OrdersCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
  navigate?: (tabId: string, params?: unknown) => void | Promise<void>;
}

interface GambioAddress {
  firstName?: string | null;
  lastName?: string | null;
  company?: string | null;
  street?: string | null;
  houseNumber?: string | null;
  postcode?: string | null;
  city?: string | null;
  country?: string | null;
  countryIsoCode?: string | null;
  additionalAddressInfo?: string | null;
}

/**
 * Eintrag des Gambio ot-Blocks (`GXOrder.totals`): Zwischensumme/Versand/Steuer/Coupon …
 * `class` = `ot_subtotal`/`ot_shipping`/`ot_tax`/`ot_total`/`ot_coupon`/… ; `value` in
 * Währungseinheiten (negativ = Rabatt/Coupon), `valueText` = formatierte Anzeige ("-500,00 EUR").
 * Vom Refresh aus dem Order-Detail in den Payload gemergt.
 */
interface GambioOrderTotal {
  title?: string | null;
  value?: number | null;
  valueText?: string | null;
  class?: string | null;
  sortOrder?: number | null;
}

/**
 * ot-Klassen, die KEINE Beleg-Modifier sind (additive Basis/Gesamt + reine Info-/Steuer-Zeilen).
 * Alles andere im ot-Block ist ein Zuschlag (positiv) oder Rabatt (negativ). Spiegelt die
 * gleichnamige Ausschlussliste im Worker (vario-order-create-from-gambio.ts).
 */
const OT_STRUCTURAL_CLASSES = new Set([
  'ot_subtotal', 'ot_subtotal_no_tax', 'ot_shipping', 'ot_tax',
  'ot_total', 'ot_total_netto', 'ot_total_net', 'ot_gm_tax_free',
]);

interface GambioPayload {
  statusName?: string | null;
  totalSum?: string | null;
  totals?: GambioOrderTotal[] | null;
  comment?: string | null;
  languageCode?: string | null;
  currencyCode?: string | null;
  customerId?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  purchaseDate?: string | null;
  paymentType?: { title?: string | null } | null;
  shippingType?: { title?: string | null } | null;
  billingAddress?: GambioAddress | null;
  deliveryAddress?: GambioAddress | null;
  [key: string]: unknown;
}

interface GambioOrderRow {
  id_order: string;
  storefront: string;
  payload: GambioPayload | null;
  fetched_at: string;
  vario_order_id: string | null;
  vario_order_number: string | null;
}

interface GambioItemAttribute {
  name?: string | null;
  value?: string | null;
  price?: number | null;
  /** Gambio: '+' = Aufpreis, '-' = Abschlag, '' = Variantenwert (kein Preiseffekt). */
  priceType?: string | null;
}

interface ItemPayload {
  finalPrice?: number | null;
  price?: number | null;
  tax?: number | null;
  quantity?: number | null;
  /** Gambio-Optionen/Varianten-Attribute der Position (z. B. Garantieverlängerung, Farbe). */
  attributes?: GambioItemAttribute[] | null;
  [key: string]: unknown;
}

interface OrderItemRow {
  id_order_item: string;
  sku: string | null;
  title: string | null;
  payload: ItemPayload | null;
}

interface MasterRow {
  orderId: string;
  storefront: string;
  date: string | null;
  customer: string | null;
  email: string | null;
  status: string | null;
  total: string | null;
  shipCountry: string | null;
  gambioCustomerId: string | null;
  varioOrderId: string | null;
  varioOrderNumber: string | null;
  payload: GambioPayload;
  importStatus: 'ok' | 'error' | null;
  /** Transient (nicht aus DB): eine Übernahme/Behebung läuft gerade → Fehleranzeige unterdrücken. */
  takeoverPending?: boolean;
  missingSkus: string[];
  /** Letzte Import-Fehlermeldung (aus activity_log.detail.error) - für die Mapping-Fehler-Erkennung. */
  importError: string | null;
}

/** Grid-API der aktuell gerenderten Orders-Tabelle - für optimistische UI-Updates aus den
 *  modul-globalen Aktions-Handlern (fixMissing/takeoverOrder). Eine Instanz pro Tab-Render. */
let ordersGridApi: GridApi<MasterRow> | null = null;

// Inline-SVGs (matchen den Mock 1:1, keine Icon-Font-Abhängigkeit für die Chrome).
const SVG_SEARCH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="m21 21-4-4"/></svg>';
const SVG_CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>';
const SVG_WARN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg>';
const SVG_GLOBE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20z"/></svg>';
const SVG_WAND = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m3 21 12-12M14.5 5.5 18 2l4 4-3.5 3.5M18 2l-2.5 2.5M20 13l1 1M9 4l1 1M4 9l1 1"/></svg>';
const SVG_IMPORT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3v12M8 11l4 4 4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></svg>';
const SVG_GEAR = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>';

// ---------------------------------------------------------------------------
// Status → neues `.dtag`-Pill-Schema. Label bleibt der Gambio-Originalname;
// nur die Farb-Klasse wird heuristisch zugeordnet (offen=amber, erledigt=grün,
// fehler=rot, sonst neutral).
// ---------------------------------------------------------------------------
function statusDtag(statusName: string | null): { cls: string; label: string } {
  const s = (statusName ?? '').trim().toLowerCase();
  if (!s) return { cls: '', label: '—' };
  if (s.includes('versendet') || s.includes('versandt') || s.includes('abgeschlossen') || s.includes('geliefert'))
    return { cls: 'status-erledigt', label: statusName! };
  if (s.includes('storniert') || s.includes('abgebrochen'))
    return { cls: 'status-fehler', label: statusName! };
  if (s.includes('zu versenden') || s.includes('wartet') || s.includes('zahlung') || s.includes('offen'))
    return { cls: 'status-offen', label: statusName! };
  return { cls: '', label: statusName! };
}

function statusPillHtml(statusName: string | null): string {
  const { cls, label } = statusDtag(statusName);
  const dot = cls ? '<span class="d"></span>' : '';
  return `<span class="dtag ${cls}">${dot}${escapeHtml(label)}</span>`;
}

/** ISO-2-Ländercode → Flaggen-Emoji (Regional-Indicator). Leerer Code → Shop-Glyph-Fallback. */
function countryFlag(iso: string | null): string {
  const code = (iso ?? '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '<span class="c-sf"><span class="v-icon fa-light fa-store" title="Shop"></span></span>';
  const flag = String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + (c.charCodeAt(0) - 65)));
  return `<span class="c-sf" title="${code}" style="font-size:15px;">${flag}</span>`;
}

export async function renderOrdersTab(
  host: HTMLElement,
  ctx: OrdersCtx,
  // Dritter Parameter ist `unknown`, damit die Funktion zur generischen Tab.render-
  // Signatur (params?: unknown) im Dashboard passt. Das `/#/orders`-Verkaufskanal-
  // Fenster reicht hier { headerHtml } durch (Mock-Header ohne Menü).
  opts?: unknown,
): Promise<void> {
  const headerHtml =
    opts && typeof opts === 'object' && 'headerHtml' in opts
      ? String((opts as { headerHtml?: string }).headerHtml ?? '')
      : '';
  host.classList.add('go-orders');
  host.innerHTML = `
    ${headerHtml}
    <div class="filterbar">
      <div class="select"><select id="gambio-orders-shop"><option value="">Alle Shops</option></select></div>
      <div class="select"><select id="gambio-orders-status"><option value="">Alle Status</option></select></div>
      <div class="tbsearch">
        ${SVG_SEARCH}
        <input type="text" id="gambio-orders-search" placeholder="Suche: Order-ID, Kunde, E-Mail" autocomplete="off" />
        <button type="button" class="clr" id="gambio-orders-search-clear" title="Suche leeren" aria-label="Suche leeren">×</button>
      </div>
      <span class="stand" id="gambio-orders-stand" aria-live="polite"
            title="Beim Öffnen des Reiters wird automatisch frisch aus dem Shop geladen, höchstens einmal pro Minute. „Aktualisieren" tut es sofort."></span>
      <button type="button" class="btn btn-primary" id="gambio-orders-refresh" title="Bestellungen frisch aus Gambio laden">
        <span class="v-icon fa-regular fa-rotate-right" id="gambio-orders-refresh-icon"></span>
        Aktualisieren
      </button>
    </div>
    <div class="work">
      <div class="card listcard">
        <div class="grid-wrap"><div id="gambio-orders-grid" class="ag-theme-vario ag-theme-quartz"></div></div>
      </div>
      <div class="split-gutter" id="gambio-orders-gutter" role="separator" aria-orientation="vertical" title="Breite ziehen (Doppelklick: zurücksetzen)"></div>
      <div class="card detailcard" id="gambio-orders-detailcard">
        <div class="dscroll" id="gambio-orders-detail"></div>
      </div>
    </div>
  `;

  const gridHost = host.querySelector<HTMLElement>('#gambio-orders-grid')!;
  const detailHost = host.querySelector<HTMLElement>('#gambio-orders-detail')!;
  const shopSel = host.querySelector<HTMLSelectElement>('#gambio-orders-shop')!;
  const statusSel = host.querySelector<HTMLSelectElement>('#gambio-orders-status')!;

  // Ziehbarer Trenner zwischen Grid (links) und Detail-Vorschau (rechts): der Nutzer stellt die
  // Balance selbst ein; die gewählte Vorschau-Breite bleibt in localStorage erhalten (Doppelklick
  // = zurücksetzen auf den CSS-Default). Grid = flex:1 (füllt den Rest), Detail = feste px-Breite.
  // ag-grid rechnet die flex-Spalten beim Container-Resize selbst neu.
  const workEl = host.querySelector<HTMLElement>('.work')!;
  const detailCard = host.querySelector<HTMLElement>('#gambio-orders-detailcard')!;
  const gutter = host.querySelector<HTMLElement>('#gambio-orders-gutter')!;
  const DETAIL_W_KEY = 'gambio.orders.detailWidthPx';
  const MIN_DETAIL = 300;
  const MIN_GRID = 420;
  const clampDetail = (px: number): number => {
    const avail = workEl.getBoundingClientRect().width || 900;
    return Math.max(MIN_DETAIL, Math.min(px, Math.max(MIN_DETAIL, avail - MIN_GRID)));
  };
  const applyDetailWidth = (px: number): void => { detailCard.style.flex = `0 0 ${Math.round(px)}px`; };
  const savedDetail = Number(localStorage.getItem(DETAIL_W_KEY));
  if (Number.isFinite(savedDetail) && savedDetail >= MIN_DETAIL) applyDetailWidth(clampDetail(savedDetail));
  let draggingSplit = false;
  gutter.addEventListener('pointerdown', (e) => {
    draggingSplit = true;
    try { gutter.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    e.preventDefault();
  });
  gutter.addEventListener('pointermove', (e) => {
    if (!draggingSplit) return;
    const rect = workEl.getBoundingClientRect();
    applyDetailWidth(clampDetail(rect.right - e.clientX)); // Detail liegt rechts
  });
  const endSplitDrag = (e: PointerEvent): void => {
    if (!draggingSplit) return;
    draggingSplit = false;
    try { gutter.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    localStorage.setItem(DETAIL_W_KEY, String(Math.round(detailCard.getBoundingClientRect().width)));
  };
  gutter.addEventListener('pointerup', endSplitDrag);
  gutter.addEventListener('pointercancel', endSplitDrag);
  gutter.addEventListener('dblclick', () => { detailCard.style.flex = ''; localStorage.removeItem(DETAIL_W_KEY); });
  const searchInput = host.querySelector<HTMLInputElement>('#gambio-orders-search')!;
  const searchClear = host.querySelector<HTMLButtonElement>('#gambio-orders-search-clear')!;
  const standEl = host.querySelector<HTMLElement>('#gambio-orders-stand')!;
  const reloadBtn = host.querySelector<HTMLButtonElement>('#gambio-orders-refresh')!;
  const reloadIcon = host.querySelector<HTMLElement>('#gambio-orders-refresh-icon')!;

  let allRows: MasterRow[] = [];

  const columns: ColDef<MasterRow>[] = [
    {
      headerName: 'SF',
      field: 'shipCountry',
      width: 56,
      pinned: 'left',
      sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: ICellRendererParams<MasterRow>) => countryFlag(p.data?.shipCountry ?? null),
    },
    {
      headerName: 'Datum',
      field: 'date',
      width: 84,
      cellRenderer: (p: ICellRendererParams<MasterRow>) =>
        `<span class="c-date">${escapeHtml(formatDateShort(p.value as string | null))}</span>`,
    },
    {
      headerName: 'Order-ID',
      field: 'orderId',
      width: 92,
      cellRenderer: (p: ICellRendererParams<MasterRow>) =>
        p.data ? `<span class="c-oid">${escapeHtml(p.data.orderId)}</span>` : '',
    },
    {
      headerName: 'Kunde',
      field: 'customer',
      flex: 1,
      minWidth: 118,
      cellRenderer: (p: ICellRendererParams<MasterRow>) => {
        const name = p.data?.customer ?? '—';
        const nr = p.data?.gambioCustomerId;
        return `<span class="c-kunde">${escapeHtml(name)}${nr ? ` <span style="color:var(--muted);font-weight:400;">· Kd-Nr. ${escapeHtml(nr)}</span>` : ''}</span>`;
      },
    },
    {
      headerName: 'Auftragsnr.',
      field: 'varioOrderNumber',
      width: 108,
      sortable: false,
      cellRenderer: (p: ICellRendererParams<MasterRow>) => {
        const d = p.data;
        const span = document.createElement('span');
        if (!d) return span;
        if (d.varioOrderNumber) {
          const url = varioDocumentUrl(d.varioOrderId);
          if (url) {
            const a = document.createElement('a');
            a.className = 'c-anr';
            a.href = url;
            a.target = '_blank';
            a.rel = 'noopener';
            a.title = 'Beleg in Vario öffnen';
            a.textContent = d.varioOrderNumber;
            span.appendChild(a);
          } else {
            span.innerHTML = `<span class="c-anr" style="border-bottom:none;">${escapeHtml(d.varioOrderNumber)}</span>`;
          }
          return span;
        }
        if (d.takeoverPending) {
          span.innerHTML = `<span style="color:var(--muted);font-style:italic;font-size:12px;display:inline-flex;align-items:center;gap:6px;"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> wird übernommen …</span>`;
          return span;
        }
        if (d.importStatus === 'error') {
          const tip = (d.missingSkus.length ? `Fehlende Artikel: ${d.missingSkus.join(', ')}` : 'Übernahme fehlgeschlagen') + ' - zum Log';
          const a = document.createElement('a');
          a.href = '#';
          a.className = 'err-flag';
          a.title = tip;
          a.style.textDecoration = 'none';
          a.style.cursor = 'pointer';
          a.innerHTML = `<span class="d"></span> Fehler`;
          a.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); void openTakeoverFailureModal(ctx, d, () => load()); });
          span.appendChild(a);
          return span;
        }
        span.innerHTML = `<span style="color:var(--muted);font-style:italic;font-size:12px;">— offen</span>`;
        return span;
      },
    },
    {
      headerName: 'Status',
      field: 'status',
      flex: 1,
      minWidth: 96,
      cellRenderer: (p: ICellRendererParams<MasterRow>) => statusPillHtml(p.data?.status ?? null),
    },
    {
      headerName: 'Aktionen',
      width: 128,
      pinned: 'right',
      sortable: false,
      resizable: false,
      cellStyle: { textAlign: 'center', padding: '0' },
      cellRenderer: (p: ICellRendererParams<MasterRow>) => {
        const d = p.data;
        const wrap = document.createElement('span');
        if (!d) return wrap;
        if (d.varioOrderNumber) {
          wrap.innerHTML = `<span class="ok-cell" title="übernommen"><span class="dot"></span><span class="ck">${SVG_CHECK}</span></span>`;
          return wrap;
        }
        const mapErr = d.importStatus === 'error' ? detectMappingError(d.importError) : null;

        // „Übernehmen"/„Erneut": startet die Übernahme nach Vario.
        const takeoverBtn = (): HTMLButtonElement => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'btn btn-primary';
          b.style.padding = '6px 11px';
          b.style.fontSize = '11.5px';
          b.title = 'In Vario übernehmen';
          const label = d.importStatus === 'error' ? 'Erneut' : 'Übernehmen';
          b.innerHTML = `${SVG_IMPORT} ${label}`;
          b.addEventListener('click', (ev) => { ev.stopPropagation(); void takeoverOrder(ctx, d, async () => { await load(); }); });
          return b;
        };

        if (d.importStatus === 'error' && d.missingSkus.length) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'fix-btn';
          b.title = 'Fehlende Artikel anlegen & übernehmen';
          b.innerHTML = `${SVG_WAND} Beheben`;
          b.addEventListener('click', (ev) => { ev.stopPropagation(); void fixMissing(ctx, d, () => load()); });
          wrap.appendChild(b);
        } else if (mapErr) {
          // Fehlende Versand-/Zahlart-Zuordnung: „Zuordnen" springt in die Einstellungen; nach dem
          // Beheben KANN direkt hier „Erneut" gedrückt werden (beide Buttons nebeneinander).
          wrap.style.display = 'flex';
          wrap.style.gap = '5px';
          wrap.style.justifyContent = 'center';
          wrap.style.alignItems = 'center';
          // „Zuordnen" kompakt als Icon-Button (Tooltip erklärt), damit „Erneut" daneben in die
          // schmale Spalte passt und das Layout nicht sprengt.
          const zBtn = document.createElement('button');
          zBtn.type = 'button';
          zBtn.className = 'fix-btn';
          zBtn.style.padding = '6px 8px';
          zBtn.setAttribute('aria-label', `${mapErr.label} zuordnen`);
          zBtn.title = `Keine Vario-${mapErr.kind === 'shipping' ? 'Versandart' : 'Zahlart'}-Zuordnung - im Einstellungen-Tab → ${mapErr.label} zuordnen`;
          zBtn.innerHTML = SVG_GEAR;
          zBtn.addEventListener('click', (ev) => { ev.stopPropagation(); openSettingsSection(mapErr.section); });
          wrap.appendChild(zBtn);
          wrap.appendChild(takeoverBtn());
        } else {
          wrap.appendChild(takeoverBtn());
        }
        return wrap;
      },
    },
  ];

  const options: GridOptions<MasterRow> = {
    columnDefs: columns,
    rowData: [],
    defaultColDef: { sortable: true, resizable: true },
    rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
    rowClassRules: {
      'row-err': (p) => p.data?.importStatus === 'error' && !p.data?.varioOrderNumber && !p.data?.takeoverPending,
    },
    overlayNoRowsTemplate:
      'Noch keine Bestellungen synchronisiert. Der Worker zieht sie per <code>gambio.refresh_orders</code>.',
    onSelectionChanged: (e) => {
      const sel = e.api.getSelectedRows()[0];
      if (!sel) {
        detailHost.innerHTML = renderDetailEmpty();
        return;
      }
      detailHost.innerHTML = renderDetailCard(sel, () => load());
      // Fehler-Banner im Detail (Beheben-Aktion) verdrahten.
      const fixBtn = detailHost.querySelector<HTMLButtonElement>('[data-gambio-detail-fix]');
      if (fixBtn) {
        fixBtn.addEventListener('click', () => void fixMissing(ctx, sel, () => load()));
      }
      const selCurrency = (typeof sel.payload.currencyCode === 'string' ? sel.payload.currencyCode : 'EUR');
      void loadPositions(ctx.supabase, sel.orderId, sel.storefront, sel.total, sel.payload.totals, detailHost, selCurrency);
      const takeoverBtn = detailHost.querySelector<HTMLButtonElement>('[data-gambio-takeover]');
      if (takeoverBtn) {
        takeoverBtn.addEventListener('click', () => {
          void takeoverOrder(ctx, sel, async () => {
            await load();
            // Order erneut selektieren, damit das Detail die neue Auftragsnr. zeigt.
            api.forEachNode((node) => { if (node.data?.orderId === sel.orderId) node.setSelected(true, true); });
          });
        });
      }
    },
  };
  const api: GridApi<MasterRow> = createGrid(gridHost, options);
  ordersGridApi = api;
  detailHost.innerHTML = renderDetailEmpty();

  function applyFilter(): void {
    const q = searchInput.value.trim().toLowerCase();
    const status = statusSel.value;
    const shop = shopSel.value;
    const rows = allRows.filter((r) => {
      if (shop && r.storefront !== shop) return false;
      if (status && (r.status ?? '') !== status) return false;
      if (!q) return true;
      return (
        r.orderId.toLowerCase().includes(q) ||
        (r.customer ?? '').toLowerCase().includes(q) ||
        (r.email ?? '').toLowerCase().includes(q)
      );
    });
    api.setGridOption('rowData', rows);
  }

  // Shop-Filter aus gambio_list_shops befüllen (storefront = shop_key). Fehler tolerant.
  async function loadShopOptions(): Promise<void> {
    const { data, error } = await ctx.supabase.rpc('gambio_list_shops');
    if (error || !Array.isArray(data)) return;
    const current = shopSel.value;
    const opts = ['<option value="">Alle Shops</option>'];
    for (const s of data as Array<{ shop_key: string; label: string | null }>) {
      opts.push(`<option value="${escapeHtml(s.shop_key)}">${escapeHtml(s.label ?? s.shop_key)}</option>`);
    }
    shopSel.innerHTML = opts.join('');
    shopSel.value = current;
  }

  function rebuildStatusOptions(): void {
    const seen = new Set<string>();
    for (const r of allRows) if (r.status) seen.add(r.status);
    const current = statusSel.value;
    const opts = ['<option value="">Alle Status</option>'];
    for (const s of [...seen].sort()) opts.push(`<option value="${escapeHtml(s)}">${escapeHtml(s)}</option>`);
    statusSel.innerHTML = opts.join('');
    if ([...seen].includes(current)) statusSel.value = current;
  }

  /** Uhrzeit fuer die Stand-Anzeige. */
  const jetztZeit = (): string =>
    new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  function setStand(text: string, spin = false): void {
    standEl.innerHTML = spin
      ? `<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> ${escapeHtml(text)}`
      : escapeHtml(text);
  }

  async function load(): Promise<void> {
    api.setGridOption('loading', true);
    reloadIcon.classList.add('fa-spin');
    const { data, error } = await ctx.supabase
      .schema('gambio')
      .from('orders')
      .select('id_order, storefront, payload, fetched_at, vario_order_id, vario_order_number')
      .order('ts_created', { ascending: false, nullsFirst: false })
      .limit(500);
    api.setGridOption('loading', false);
    reloadIcon.classList.remove('fa-spin');
    if (error) {
      openErrorModal({
        title: 'Bestellungen nicht ladbar',
        items: [{ message: error.message }],
        context: { action: 'gambio_load_orders' },
        json: error,
      });
      allRows = [];
      api.setGridOption('rowData', []);
      return;
    }
    allRows = (data ?? []).map((o) => toRow(o as GambioOrderRow));

    // Merge latest order_import log status per order.
    const orderIds = allRows.map((r) => r.orderId);
    if (orderIds.length) {
      const { data: logs } = await ctx.supabase
        .schema('gambio')
        .from('activity_log')
        .select('ref_id,storefront,status,detail,created_at')
        .eq('event', 'order_import')
        .in('ref_id', orderIds)
        .order('created_at', { ascending: false });
      const latest = new Map<string, { status: string; detail: Record<string, unknown> | null }>();
      for (const l of (logs ?? []) as Array<{ ref_id: string; storefront: string; status: string; detail: Record<string, unknown> | null }>) {
        const key = `${l.storefront} ${l.ref_id}`;
        if (!latest.has(key)) latest.set(key, { status: l.status, detail: l.detail });
      }
      for (const r of allRows) {
        const e = latest.get(`${r.storefront} ${r.orderId}`);
        r.importStatus = e ? (e.status === 'error' ? 'error' : 'ok') : null;
        const ms = e?.detail?.['missingSkus'];
        r.missingSkus = Array.isArray(ms) ? (ms as string[]) : [];
        const errMsg = e?.detail?.['error'];
        r.importError = typeof errMsg === 'string' ? errMsg : null;
      }
    }

    rebuildStatusOptions();
    applyFilter();
    // Die Liste kommt aus der Datenbank - das ist der "Stand". Ein Abruf aus dem Shop haengt
    // danach seine eigene Zusammenfassung an (siehe refreshFromGambio).
    setStand(`Stand ${jetztZeit()}`);
  }

  // „Aktualisieren" zieht FRISCH aus Gambio: pro (gefiltertem) Shop einen
  // gambio.refresh_orders-Job enqueuen (RPC, idempotent), auf Abschluss warten,
  // dann die lokale Liste neu laden. Ohne Shop-Filter → alle Shops aus dem Dropdown.
  async function refreshFromGambio(opts?: { silent?: boolean }): Promise<void> {
    const silent = opts?.silent === true;
    const shopsAusAuswahl = (): string[] =>
      Array.from(shopSel.options).map((o) => o.value).filter(Boolean);
    let targets = shopSel.value ? [shopSel.value] : shopsAusAuswahl();
    if (targets.length === 0) {
      // Leere Shop-Auswahl war der EINE Pfad, auf dem gar nichts passierte und auch nichts zu
      // sehen war: die Auswahl traegt nur "Alle Shops" mit leerem Wert, bis loadShopOptions durch
      // ist - und dauerhaft, wenn dessen RPC fehlschlug. Also nachladen statt still aufgeben.
      await loadShopOptions();
      targets = shopsAusAuswahl();
    }
    if (targets.length === 0) {
      await load();
      setStand('Kein Shop zugeordnet');
      if (!silent) showToast('Kein Gambio-Shop zugeordnet - es gibt nichts zu aktualisieren.', 'info');
      return;
    }
    reloadBtn.disabled = true;
    reloadIcon.classList.add('fa-spin');
    setStand('Lade aus Gambio …', true);
    if (!silent) showToast('Bestellungen werden aus Gambio geladen …', 'info');
    let upserted = 0;
    let failed = 0;
    let jobs = 0;
    try {
      const jobIds: string[] = [];
      for (const shopKey of targets) {
        const { data, error } = await ctx.supabase.rpc('gambio_enqueue_orders_refresh', { _shop_key: shopKey });
        if (error) {
          failed++;
          // Beim automatischen Lauf KEIN Modal - das poppt sonst beim Reiterwechsel auf. Aber
          // auch nicht verschweigen, sonst sieht man einen gescheiterten Abruf ueberhaupt nicht.
          if (silent) showToast(`Bestellungen konnten nicht abgerufen werden: ${error.message}`, 'error');
          else openErrorModal({
            title: `Refresh „${shopKey}" fehlgeschlagen`,
            items: [{ message: error.message }],
            context: { action: 'gambio_enqueue_orders_refresh', shopKey },
            json: error,
          });
          continue;
        }
        const rec = Array.isArray(data) ? data[0] : data;
        const id = (rec as { job_id?: string } | undefined)?.job_id;
        if (id) jobIds.push(id);
      }
      const rows = await Promise.all(
        jobIds.map((id) => awaitJob(ctx.supabase, id, 60_000).catch(() => null)),
      );
      for (const row of rows) {
        if (!row) { failed++; continue; }
        jobs++;
        const res = row.result as { ordersUpserted?: unknown } | null;
        const n = Number(res?.ordersUpserted ?? 0);
        if (Number.isFinite(n)) upserted += n;
      }
      if (jobIds.length && !silent) showToast('Bestellungen aktualisiert.', 'success');
    } finally {
      reloadBtn.disabled = false;
      // load() setzt seinen eigenen "Stand" - die Zusammenfassung des Abrufs kommt danach.
      await load();
    }
    setStand(ordersRefreshSummary({ time: jetztZeit(), upserted, failed, jobs }));
  }

  reloadBtn.addEventListener('click', () => void refreshFromGambio());

  shopSel.addEventListener('change', applyFilter);
  statusSel.addEventListener('change', applyFilter);
  searchInput.addEventListener('input', applyFilter);
  searchClear.addEventListener('click', () => {
    searchInput.value = '';
    applyFilter();
    searchInput.focus();
  });

  // Deep-Link aus dem Protokoll VOR focusOrderFromUrl merken - die Funktion raeumt die Parameter
  // aus der Adresse. Wer gezielt EINE Bestellung ansehen will, soll sie nicht durch einen
  // nachgelagerten Abruf unter den Haenden weggeladen bekommen.
  const cameFromDeepLink = new URLSearchParams(window.location.search).has('gambioOrderId');
  const shopOptionsReady = loadShopOptions();
  await load();
  focusOrderFromUrl(api);

  // Betreiber-Wunsch 2026-08-28: Wer den Reiter "Bestellungen" aufmacht, will den aktuellen Stand
  // sehen. Die Liste oben kommt aus der Datenbank; frisch aus dem Shop holt erst der
  // gambio.refresh_orders-Job. Also einmal automatisch anstossen - gedrosselt, weil VARIO das
  // iframe bei JEDEM Reiterwechsel neu aufbaut und ein ungedrosselter Lauf sonst je Shop einen
  // Job pro Klick erzeugt. Still: der spinnende Knopf zeigt es an, Toasts bleiben dem manuellen
  // "Aktualisieren" vorbehalten.
  async function maybeAutoRefresh(grund: string): Promise<void> {
    if (cameFromDeepLink) {
      console.info('[gambio-orders] Auto-Aktualisierung uebersprungen: Deeplink auf eine Bestellung');
      return;
    }
    let allowed = true;
    try {
      allowed = shouldAutoRefreshOrders(sessionStorage.getItem(ORDERS_AUTO_REFRESH_KEY), Date.now());
      if (allowed) sessionStorage.setItem(ORDERS_AUTO_REFRESH_KEY, String(Date.now()));
    } catch {
      /* kein sessionStorage (privater Modus) -> einmal pro Aufbau abrufen */
    }
    if (!allowed) {
      console.info(`[gambio-orders] Auto-Aktualisierung uebersprungen (${grund}): Drosselung (max. 1x/Minute)`);
      return;
    }
    await shopOptionsReady;
    console.info(`[gambio-orders] Auto-Aktualisierung gestartet (${grund})`);
    try {
      await refreshFromGambio({ silent: true });
    } catch (e) {
      // Ein unbeachteter Fehler war hier vorher voellig unsichtbar: kein Toast, kein Log, nichts.
      console.error('[gambio-orders] Auto-Aktualisierung fehlgeschlagen', e);
      showToast(`Automatisches Aktualisieren fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`, 'error');
    }
  }

  void maybeAutoRefresh('Aufbau');

  // ZWEITER Ausloeser, und er ist kein Luxus: baut VARIO das iframe beim Reiterwechsel NICHT neu
  // auf, sondern blendet es nur aus und wieder ein, laeuft der Aufbau oben genau einmal - beim
  // Zurueckkommen auf den Reiter passiert dann nichts. Deshalb zusaetzlich auf "wieder sichtbar"
  // reagieren:
  //   - visibilitychange deckt den Browser-Tab ab (anderer Tab, minimiertes Fenster),
  //   - der IntersectionObserver deckt display:none / Aus- und Einblenden im Vario-Layout ab,
  //     was visibilitychange NICHT sieht (die Sichtbarkeit gilt pro Dokument, nicht pro Element).
  // Die Drosselung in maybeAutoRefresh haelt beides im Zaum.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void maybeAutoRefresh('Tab wieder sichtbar');
  });
  if (typeof IntersectionObserver !== 'undefined') {
    let warUnsichtbar = false;
    new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) { warUnsichtbar = true; continue; }
        if (warUnsichtbar) {
          warUnsichtbar = false;
          void maybeAutoRefresh('Reiter wieder eingeblendet');
        }
      }
    }).observe(host);
  }
}

/**
 * Vorauswahl aus dem Log: `?gambioOrderId=…&gambioOrderShop=…` (gesetzt von `openOrdersSlot` in
 * `tabs/log.ts`) selektiert die Bestellung und scrollt sie in den Blick. `setSelected` löst
 * `onSelectionChanged` aus, das Detail baut sich also von selbst auf.
 *
 * Die Parameter werden danach aus der Adresse entfernt (`replaceState`), sonst würde „Aktualisieren"
 * immer wieder auf dieselbe Zeile zurückspringen. Nicht gefunden heißt in der Regel: älter als die
 * 500 geladenen Bestellungen oder aus einem anderen Shop - dann ein Hinweis statt stiller Nichts-Tun.
 */
function focusOrderFromUrl(api: GridApi<MasterRow>): void {
  const params = new URLSearchParams(window.location.search);
  const wanted = params.get('gambioOrderId');
  if (!wanted) return;
  const wantedShop = params.get('gambioOrderShop');

  params.delete('gambioOrderId');
  params.delete('gambioOrderShop');
  const q = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${q ? `?${q}` : ''}${window.location.hash}`);

  let hit = false;
  api.forEachNode((node) => {
    if (hit || node.data?.orderId !== wanted) return;
    if (wantedShop && node.data.storefront !== wantedShop) return;
    node.setSelected(true, true);
    api.ensureNodeVisible(node, 'middle');
    hit = true;
  });
  if (!hit) showToast(`Bestellung ${wanted} ist in dieser Liste nicht enthalten.`, 'info');
}

// ---------------------------------------------------------------------------

function toRow(o: GambioOrderRow): MasterRow {
  const p = (o.payload ?? {}) as GambioPayload;
  const str = (v: unknown): string | null => (typeof v === 'string' && v.length ? v : null);
  const ship = p.deliveryAddress?.countryIsoCode ?? p.billingAddress?.countryIsoCode ?? null;
  return {
    orderId: o.id_order,
    storefront: o.storefront, // kein ColDef → erscheint nicht im Grid, nur für Positions-Lookup
    date: str(p.purchaseDate),
    customer: str(p.customerName),
    email: str(p.customerEmail),
    status: str(p.statusName),
    total: str(p.totalSum),
    shipCountry: ship,
    gambioCustomerId: str(p.customerId),
    varioOrderId: o.vario_order_id ?? null,
    varioOrderNumber: o.vario_order_number ?? null,
    payload: p,
    importStatus: null,
    missingSkus: [],
    importError: null,
  };
}

interface MappingErrorInfo { kind: 'shipping' | 'payment'; section: 'versandarten' | 'zahlungsarten'; label: string; }

/**
 * Erkennt einen fehlenden-Zuordnungs-Fehler (Versand-/Zahlart) an der Worker-Meldung
 * (`vario-order-create-from-gambio.ts`). Solche Fehler sind NICHT durch „Erneut" lösbar - erst muss
 * im Einstellungen-Slot die Zuordnung gepflegt werden. Rückgabe steuert Button-Label + Ziel-Sektion.
 */
function detectMappingError(msg: string | null | undefined): MappingErrorInfo | null {
  if (!msg) return null;
  // Deckt beide Quellen ab: die von der App formulierten Zuordnungs-Fehler UND die uebersetzten
  // Vario-Ablehnungen (fehlende Liefer-/Zahlungsbedingung, siehe lib/gambio-error-message.ts).
  if (msg.includes('Versandart-Zuordnung') || msg.includes('Versandarten') || msg.includes('Lieferbedingung')) return { kind: 'shipping', section: 'versandarten', label: 'Versandarten' };
  if (msg.includes('Zahlart-Zuordnung') || msg.includes('Zahlungsart') || msg.includes('Zahlungsbedingung')) return { kind: 'payment', section: 'zahlungsarten', label: 'Zahlungsarten' };
  return null;
}

/**
 * Öffnet die Einstellungen mit fokussierter Sektion (z. B. Versandarten).
 *
 * ZWEI Wege, in dieser Reihenfolge:
 *  1. `openInSameTab` an den VARIO-Host — dann wechselt auch die Registerkarte OBEN auf
 *     „Einstellungen". Die Tab-Leiste gehört VARIO; eine interne Navigation ändert nur unseren
 *     Inhalt, und der Betreiber sass mit „Bestellungen" im Reiter vor den Einstellungen
 *     (Befund 2026-08-28).
 *  2. Rückfall nach kurzer Frist: wie bisher das iframe selbst auf `/#/settings` navigieren.
 *     Führt der Host den Wechsel aus, tauscht er das iframe aus und dieser Timer läuft nie ab —
 *     Vario entfernt das iframe beim Reiterwechsel. Ignoriert er die Nachricht, landet der
 *     Betreiber trotzdem an der richtigen Stelle statt auf einem toten Knopf.
 *
 * Die Ziel-Sektion überlebt den Host-Wechsel über `sessionStorage` (gleicher Origin, die
 * Query-Parameter setzt beim Tab-Wechsel VARIO). Beim eigenen Rückfall bleibt zusätzlich der
 * Query-Parameter, und der `appToken` in der URL bleibt erhalten → keine Re-Authentifizierung.
 *
 * Seit 2026-09-14 liegt „Einstellungen" NICHT mehr im Verkaufskanal, sondern in der Appverwaltung
 * (`system.apps.{appId}.tabs`). Ziel ist deshalb die ERP-Route der App-Detailseite
 * (`openVarioAppManagementTab` → `system.apps.detail.app`), nicht der generierte Schwester-Name mit
 * den Verkaufskanal-Parametern; Herleitung und Live-Test in `lib/vario-route.ts`.
 */
function openSettingsSection(section: string): void {
  try {
    sessionStorage.setItem(SETTINGS_SECTION_HANDOVER, section);
  } catch {
    /* privater Modus o. Ä. — dann greift der Query-Parameter des Rückfalls */
  }
  const navigateSelf = (): void => {
    const url = new URL(window.location.href);
    url.searchParams.set('gambioSettingsSection', section);
    url.hash = '#/settings';
    window.location.assign(url.toString());
  };
  if (!openVarioAppManagementTab('gambio-settings')) {
    navigateSelf();
    return;
  }
  window.setTimeout(navigateSelf, 600);
}

function renderDetailEmpty(): string {
  return `
    <div style="height:100%;min-height:440px;display:flex;align-items:center;justify-content:center;text-align:center;color:var(--muted);">
      <div>
        <div style="font-size:2.4em;margin-bottom:0.3em;opacity:0.7;">
          <span class="v-icon fa-light fa-clipboard-list"></span>
        </div>
        Bestellung links auswählen, um die Details zu sehen.
      </div>
    </div>
  `;
}

function renderDetailCard(row: MasterRow, _onFixed: () => void | Promise<void>): string {
  const p = row.payload;
  const belegUrl = varioDocumentUrl(row.varioOrderId);

  // Bestellsprache aus dem Gambio-Payload (vom Refresh aus dem Order-Detail angereichert) als
  // Badge - z. B. "EN". Leerer/fehlender Code → kein Badge.
  const lang = (typeof p.languageCode === 'string' ? p.languageCode : '').trim().toUpperCase();
  const currency = (typeof p.currencyCode === 'string' ? p.currencyCode : 'EUR');
  const delivery = p.deliveryAddress ?? null;
  const billing = p.billingAddress ?? null;
  const sameAddress = addressesEqual(delivery, billing);
  const billingBox = sameAddress
    ? `<div class="dbox"><div class="addr" style="color:var(--muted);font-style:italic;">identisch mit Lieferadresse</div></div>`
    : `<div class="dbox">${renderAddress(billing, null)}</div>`;

  const payment = p.paymentType?.title?.trim() || '—';
  const shipping = p.shippingType?.title?.trim() || '—';
  const shipCountry = (row.shipCountry ?? '').trim().toUpperCase();

  // Auftrags-Tag (übernommen, Deeplink) bzw. Übernehmen-Button.
  const auftragBit = row.varioOrderNumber
    ? (belegUrl
        ? `<a href="${escapeHtml(belegUrl)}" target="_blank" rel="noopener" class="dtag auftrag" title="Beleg in Vario öffnen" style="text-decoration:none;"><span class="d"></span>${escapeHtml(row.varioOrderNumber)}</a>`
        : `<span class="dtag auftrag" title="Vario-Auftrag"><span class="d"></span>${escapeHtml(row.varioOrderNumber)}</span>`)
    : `<button type="button" class="btn btn-primary" data-gambio-takeover title="Bestellung als Vario-Auftrag übernehmen">
         <span class="v-icon fa-regular fa-file-import" data-takeover-icon></span> In Vario übernehmen
       </button>`;
  const langBit = lang ? `<span class="dtag" title="Bestellsprache">${SVG_GLOBE.replace('<svg', '<svg style="width:13px;height:13px;"')} ${escapeHtml(lang)}</span>` : '';
  const landBit = shipCountry ? `<span class="dtag land" title="Lieferland"><span class="d"></span>${escapeHtml(shipCountry)}</span>` : '';

  // Fehler-Banner, wenn die letzte Übernahme fehlschlug und noch keine Auftragsnr. existiert.
  const errBanner = (row.importStatus === 'error' && !row.varioOrderNumber)
    ? `<div class="errbanner">
         <div class="ic">${SVG_WARN}</div>
         <div>
           <div class="t">Übernahme fehlgeschlagen</div>
           <div class="s">${row.missingSkus.length ? `Fehlende Artikel: ${escapeHtml(row.missingSkus.join(', '))}` : 'Die Bestellung konnte nicht nach Vario übernommen werden.'}</div>
         </div>
         ${row.missingSkus.length ? `<div class="act"><button type="button" class="fix-btn" data-gambio-detail-fix>${SVG_WAND} Beheben</button></div>` : ''}
       </div>`
    : '';

  return `
    <div class="dhead">
      <span class="lbl">Bestellung</span>
      <span class="oid">${escapeHtml(row.orderId)}</span>
      ${row.date ? `<span class="time">${escapeHtml(formatDate(row.date))}</span>` : ''}
      ${row.gambioCustomerId ? `<span class="time">· Kd-Nr. ${escapeHtml(row.gambioCustomerId)}</span>` : ''}
      <span class="sp"></span>
      <div class="tags">
        ${auftragBit}
        ${landBit}
        ${langBit}
        ${statusPillHtml(row.status)}
      </div>
    </div>
    ${errBanner}
    <div class="dcols">
      <div class="dsec">
        <div class="lab">Lieferadresse</div>
        <div class="dbox">${renderAddress(delivery, row.email)}</div>
      </div>
      <div class="dsec">
        <div class="lab">Rechnungsadresse</div>
        ${billingBox}
      </div>
    </div>
    <div class="dcols">
      <div class="dsec">
        <div class="lab">Gesamtpreis</div>
        <div class="dbox" id="gambio-totals">${renderTotalsBlock([], row.total, p.totals, currency)}</div>
      </div>
      <div class="dsec">
        <div class="lab">Zahlung &amp; Versand</div>
        <div class="dbox">
          <div class="zv-row"><span class="k">Zahlart</span><span class="v">${escapeHtml(payment)}</span></div>
          <div class="zv-row"><span class="k">Versandart</span><span class="v">${escapeHtml(shipping)}</span></div>
        </div>
      </div>
    </div>
    <div class="dsec">
      <div class="lab">Positionen ${infoBadge('Zeigt Positionen, Rabatte, Versand und Steuern, bevor der Beleg erzeugt wird.')}</div>
      <div id="gambio-positions">
        <div style="color:var(--muted);display:flex;align-items:center;gap:0.5em;padding:6px 0;">
          <span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Positionen werden geladen…
        </div>
      </div>
    </div>
  `;
}

async function loadPositions(
  supabase: SupabaseClient,
  orderId: string,
  storefront: string,
  totalSumStr: string | null,
  totals: GambioOrderTotal[] | null | undefined,
  detailHost: HTMLElement,
  currency: string = 'EUR',
): Promise<void> {
  const { data, error } = await supabase
    .schema('gambio')
    .from('order_items')
    .select('id_order_item, sku, title, payload')
    .eq('id_order', orderId)
    .eq('storefront', storefront)
    .order('title', { ascending: true });

  // Container kann inzwischen weg sein (andere Order ausgewählt) → still no-op.
  const container = detailHost.querySelector<HTMLElement>('#gambio-positions');
  if (!container) return;

  if (error) {
    container.innerHTML = `<div style="color:var(--muted);font-style:italic;">Positionen konnten nicht geladen werden.</div>`;
    return;
  }
  const items = (data ?? []) as OrderItemRow[];

  // Vario-Artikel-IDs der Positionen für Deeplinks (sku → vario_article_id über article_mappings,
  // analog zum Order-Import-Matching). Best-effort: schlägt der Lookup fehl, bleibt die SKU ohne Link.
  const skus = Array.from(new Set(items.map((it) => it.sku).filter((s): s is string => !!s)));
  const articleIdBySku = new Map<string, string>();
  if (skus.length > 0) {
    const { data: maps } = await supabase
      .schema('vario')
      .from('article_mappings')
      .select('vario_article_number, vario_article_id')
      .eq('marketplace', 'gambio')
      .eq('storefront', storefront)
      .in('vario_article_number', skus);
    for (const m of (maps ?? []) as Array<{ vario_article_number: string | null; vario_article_id: string | null }>) {
      if (m.vario_article_number && m.vario_article_id) articleIdBySku.set(m.vario_article_number, m.vario_article_id);
    }
  }

  // Summenblock neu rendern: bevorzugt aus dem ot-Block (subtotal/shipping/discounts/total), sonst
  // als Fallback aus den jetzt geladenen Positionen (Bestellsumme = Σ finalPrice, Versand = Residuum).
  const totalsEl = detailHost.querySelector<HTMLElement>('#gambio-totals');
  if (totalsEl) totalsEl.innerHTML = renderTotalsBlock(items, totalSumStr, totals, currency);

  if (items.length === 0) {
    container.innerHTML = `<div style="color:var(--muted);font-style:italic;">Keine Positionen.</div>`;
    return;
  }
  container.innerHTML = renderPositionsTable(items, articleIdBySku, currency);
}

/**
 * Optimistische UI beim Start einer Übernahme/Behebung: entfernt eine (evtl. noch von einem
 * früheren Versuch stammende) „Übernahme fehlgeschlagen"-Anzeige SOFORT und zeigt „läuft" —
 * damit während des ~1 min laufenden Jobs keine irreführende Fehlermeldung stehenbleibt. Der
 * echte Endzustand wird nach Abschluss über load() gerendert. Rein visuell/transient.
 */
function beginTakeoverUi(row: MasterRow): void {
  row.takeoverPending = true;
  // Grid: rotes „Fehler"-Flag + row-err fallen weg, die Zelle zeigt „wird übernommen …".
  ordersGridApi?.redrawRows();
  // Detail-Fehlerbanner (falls sichtbar) neutral auf „läuft" umschalten.
  const banner = document.querySelector<HTMLElement>('.errbanner');
  if (banner) {
    banner.classList.add('errbanner--pending');
    banner.innerHTML =
      `<div class="ic"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span></div>` +
      `<div><div class="t">Übernahme läuft …</div>` +
      `<div class="s">Bestellung ${escapeHtml(row.orderId)} wird nach Vario übernommen.</div></div>`;
  }
}

async function fixMissing(ctx: OrdersCtx, row: MasterRow, after: () => void | Promise<void>): Promise<void> {
  showToast('Fehlende Artikel werden angelegt …', 'info');
  beginTakeoverUi(row);
  try {
    const baselineId = await latestActivityId(ctx.supabase, row.orderId, row.storefront);
    const { error } = await ctx.supabase.rpc('gambio_enqueue_fix_missing_articles', {
      _shop_key: row.storefront, _id_order: row.orderId,
    });
    if (error) throw error;
    // Auf das tatsächliche Auftrags-Ergebnis warten - der Fix-Job legt nur die Artikel an und
    // stößt den Auftrags-Re-Import als SEPARATEN Job an (siehe Spec). 90s Poll @ 3s.
    const outcome = await awaitOrderOutcome(ctx.supabase, {
      shopKey: row.storefront, idOrder: row.orderId, baselineId,
    });
    if (outcome.status === 'success') {
      showToast(outcome.beleg ? `Bestellung ${row.orderId} übernommen - Beleg ${outcome.beleg}` : `Bestellung ${row.orderId} übernommen.`, 'success');
    } else if (outcome.status === 'error') {
      openErrorModal({
        title: 'Behebung fehlgeschlagen',
        items: [{ message: outcome.summary }],
        context: { action: 'gambio_fix_missing_articles', shopKey: row.storefront, orderId: row.orderId },
        json: outcome,
      });
    } else {
      showToast(`Behebung für ${row.orderId} läuft noch - siehe Log.`, 'info');
    }
  } catch (e) {
    openErrorModal({
      title: 'Behebung fehlgeschlagen',
      items: [{ message: (e as Error).message }],
      context: { action: 'gambio_fix_missing_articles', shopKey: row.storefront, orderId: row.orderId },
      json: { message: (e as Error).message, stack: (e as Error).stack },
    });
  } finally {
    await after(); // Liste immer aktualisieren
  }
}

/**
 * Kuratiertes Fehler-Modal für eine gescheiterte Bestellübernahme (fehlende Artikel und/oder
 * Versand-/Zahlart-Mapping) mit Beheben-/Zuordnen-/Erneut-Aktionen. Bewusst NICHT der generische
 * `openErrorModal` aus dem Design-System - dieser Fall hat bereits eine kuratierte, handlungsfähige
 * UI; ein zusätzlicher roher Fehler-Dump wäre hier redundant.
 */
async function openTakeoverFailureModal(ctx: OrdersCtx, row: MasterRow, onFixed: () => void | Promise<void>): Promise<void> {
  const { data } = await ctx.supabase
    .schema('gambio').from('activity_log')
    .select('summary,detail,created_at')
    .eq('event', 'order_import').eq('status', 'error')
    .eq('storefront', row.storefront).eq('ref_id', row.orderId)
    .order('created_at', { ascending: false }).limit(1);
  const entry = (data ?? [])[0] as { summary: string | null; detail: Record<string, unknown> | null } | undefined;
  const summary = entry?.summary ?? 'Übernahme fehlgeschlagen';
  const canFix = row.missingSkus.length > 0;
  // Mapping-Fehler aus der Meldung ableiten (detail.error bevorzugt, sonst summary) → Direktlink.
  const detailErr = typeof entry?.detail?.['error'] === 'string' ? (entry.detail['error'] as string) : null;
  const mapErr = detectMappingError(detailErr ?? summary ?? row.importError);
  // Farbig hinterlegter Fehler-Banner (Danger-Tokens) statt kl-addr-Karte; KEIN roher JSON-Dump.
  const dangerBox = 'background:var(--st-danger-bg);border:1px solid #FECDCA;border-left:4px solid var(--st-danger);color:var(--st-danger);border-radius:10px;padding:13px 15px;font-size:13.5px;line-height:1.5;display:flex;gap:10px;align-items:flex-start;';
  const infoBox = 'background:var(--st-neutral-bg);border:1px solid var(--v-border-strong,#D0D5DD);color:var(--st-neutral,#475467);border-radius:10px;padding:11px 14px;margin-top:10px;font-size:13px;line-height:1.5;';
  const modal = openModal({
    title: `Übernahme fehlgeschlagen - Bestellung ${row.orderId}`,
    icon: 'fa-triangle-exclamation',
    // 640 statt 560: bei drei Knoepfen (Schliessen / Zuordnung oeffnen / Uebernehmen) lief
    // „Schliessen" links aus dem Dialog heraus. Die Fusszeile bricht zusaetzlich um (app.css).
    width: 640,
    body: `<div style="${dangerBox}"><span class="v-icon fa-regular fa-circle-exclamation" style="margin-top:1px;font-size:15px;"></span><span>${escapeHtml(summary)}</span></div>`
      + (row.missingSkus.length ? `<div style="${infoBox}"><strong>Fehlende Artikel:</strong> ${escapeHtml(row.missingSkus.join(', '))}</div>` : '')
      + (mapErr ? `<div style="${infoBox}">Diese Bestellung lässt sich erst übernehmen, wenn die betroffene ${mapErr.kind === 'shipping' ? 'Versandart' : 'Zahlart'} einer Vario-${mapErr.kind === 'shipping' ? 'Versandart' : 'Zahlart'} zugeordnet ist. „Erneut" allein hilft nicht.</div>` : ''),
    footer:
      `<button type="button" class="v-button" data-modal-cancel>Schließen</button>` +
      (mapErr ? `<button type="button" class="v-button" data-goto-settings><span class="v-icon fa-regular fa-sliders"></span> ${escapeHtml(mapErr.label)} zuordnen</button>` : '') +
      (canFix ? `<button type="button" class="v-button v-button-primary" data-fix><span class="v-icon fa-regular fa-wand-magic-sparkles"></span> Fehlende Artikel anlegen &amp; übernehmen</button>` : '') +
      (!canFix ? `<button type="button" class="v-button v-button-primary" data-retry><span class="v-icon fa-regular fa-file-import"></span> Erneut übernehmen</button>` : ''),
    onMount: (rootEl) => {
      const fixBtn = rootEl.querySelector<HTMLButtonElement>('[data-fix]');
      if (fixBtn) fixBtn.addEventListener('click', () => { modal.close(); void fixMissing(ctx, row, onFixed); });
      const gotoBtn = rootEl.querySelector<HTMLButtonElement>('[data-goto-settings]');
      if (gotoBtn && mapErr) gotoBtn.addEventListener('click', () => { modal.close(); openSettingsSection(mapErr.section); });
      const retryBtn = rootEl.querySelector<HTMLButtonElement>('[data-retry]');
      if (retryBtn) retryBtn.addEventListener('click', () => { modal.close(); void takeoverOrder(ctx, row, async () => { await onFixed(); }); });
    },
  });
}

async function takeoverOrder(ctx: OrdersCtx, row: MasterRow, onDone: () => Promise<void>): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>('[data-gambio-takeover]');
  const icon = document.querySelector<HTMLElement>('[data-takeover-icon]');
  if (btn) btn.disabled = true;
  if (icon) icon.className = 'v-icon fa-regular fa-spinner-third fa-spin';
  showToast('Bestellung wird nach Vario übernommen …', 'info');
  beginTakeoverUi(row);
  try {
    const { data, error } = await ctx.supabase.rpc('gambio_enqueue_order_create', {
      _shop_key: row.storefront,
      _id_order: row.orderId,
    });
    if (error) throw new Error(error.message);
    const rec = Array.isArray(data) ? data[0] : data;
    const jobId = rec?.job_id as string | undefined;
    // 180s wie der Kaufland-Order-Create: identischer mehrstufiger Vario-Flow inkl.
    // Lock-Retry-Backoff. Kürzer riskiert eine falsch-negative "fehlgeschlagen"-Meldung,
    // obwohl der Worker vario_order_id mitten im Lauf bereits persistiert.
    const job = jobId ? await awaitJob(ctx.supabase, jobId, 180_000) : null;
    if (job && job.status !== 'completed') {
      throw new Error(job.error || 'Übernahme fehlgeschlagen.');
    }
    showToast('Bestellung übernommen.', 'success');
  } catch (e) {
    const msg = (e as Error).message;
    if (btn) btn.disabled = false;
    if (icon) icon.className = 'v-icon fa-regular fa-file-import';
    // Fehlende Versand-/Zahlart-Zuordnung → direkt das kuratierte Modal mit Direktlink zur
    // Zuordnungs-Seite öffnen (bloßes „Erneut" hilft nicht); der generische Fehler-Popup wäre
    // hier redundant. row.importError für die Modal-Erkennung mitgeben.
    if (detectMappingError(msg)) {
      row.importError = msg;
      void openTakeoverFailureModal(ctx, row, onDone);
    } else {
      openErrorModal({
        title: 'Übernahme fehlgeschlagen',
        items: [{ message: msg }],
        context: { action: 'gambio_takeover_order', shopKey: row.storefront, orderId: row.orderId },
        json: { message: msg, stack: (e as Error).stack },
      });
    }
  } finally {
    // Liste IMMER aktualisieren (Erfolg → Beleg, Fehler → ⚠ Fehler-Pille) - sonst
    // bliebe der alte Status stehen, bis der Nutzer manuell „Aktualisieren" klickt.
    await onDone();
  }
}

/** Bestellsumme = Σ Zeilensummen (finalPrice bzw. price×quantity, brutto). null wenn nichts ermittelbar. */
function computeSubtotal(items: OrderItemRow[]): number | null {
  let sum = 0;
  let any = false;
  for (const it of items) {
    const qty = typeof it.payload?.quantity === 'number' ? it.payload.quantity : null;
    const unit = typeof it.payload?.price === 'number' ? it.payload.price : null;
    const line =
      typeof it.payload?.finalPrice === 'number'
        ? it.payload.finalPrice
        : unit !== null && qty !== null
          ? unit * qty
          : null;
    if (line !== null) {
      sum += line;
      any = true;
    }
  }
  return any ? sum : null;
}

/** Gambio `totalSum` ist ein formatierter String ("68,70 EUR", "1.234,56 €") → Zahl. */
function parseMoney(s: string | null): number | null {
  if (!s) return null;
  const cleaned = s.replace(/[^\d,.-]/g, '').trim();
  if (!cleaned) return null;
  // Deutsches Format: '.' = Tausender, ',' = Dezimal. Nur wenn ',' vorhanden umstellen.
  const norm = cleaned.includes(',') ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned;
  const n = Number(norm);
  return Number.isFinite(n) ? n : null;
}

/** Betrag eines ot-Eintrags: bevorzugt aus `valueText` (autoritativ inkl. Nachkommastellen), sonst `value`. */
function otAmount(t: GambioOrderTotal): number | null {
  const fromText = typeof t.valueText === 'string' ? parseMoney(t.valueText) : null;
  if (fromText !== null) return fromText;
  return typeof t.value === 'number' && Number.isFinite(t.value) ? t.value : null;
}

/** Entfernt HTML-Tags aus ot-Titeln (z. B. ot_total trägt "<b>Summe</b>:"). */
function stripTags(s: string | null | undefined): string {
  return (s ?? '').replace(/<[^>]*>/g, '').trim();
}

/**
 * Summenblock: Bestellsumme / (Rabatte + Zuschläge) / Versandkosten / Gesamtpreis. Quelle der Wahrheit
 * ist der Gambio ot-Block (`payload.totals`, vom Refresh angereichert): Bestellsumme aus `ot_subtotal`,
 * Versand aus `ot_shipping`, Gesamtpreis aus `ot_total`; JEDER nicht-strukturelle ot-Posten
 * (OT_STRUCTURAL_CLASSES ausgenommen) wird eine eigene Zeile - negativ = Rabatt (Coupon/Gutschein),
 * positiv = Zuschlag (Nachnahme, Mindermengen-/Versandkostenzuschlag …), sortiert nach `sortOrder`.
 * Ohne ot-Block (Alt-Order vor Refresh) fällt es auf das alte Verfahren zurück (Bestellsumme aus
 * Positionen, Versand = Gesamt − Bestellsumme − Σ signierte Modifier). Solange ohne ot-Block die
 * Positionen noch nicht geladen sind (leeres Array), bleiben Bestellsumme/Versand "—".
 */
function renderTotalsBlock(
  items: OrderItemRow[],
  totalSumStr: string | null,
  totals?: GambioOrderTotal[] | null,
  currency: string = 'EUR',
): string {
  const ot = Array.isArray(totals) ? totals : [];
  const byClass = (cls: string): GambioOrderTotal | undefined => ot.find((t) => (t.class ?? '') === cls);

  // Modifier = alle nicht-strukturellen ot-Posten (Rabatte negativ + Zuschläge positiv), nach sortOrder.
  const modifiers = ot
    .filter((t) => !OT_STRUCTURAL_CLASSES.has(t.class ?? ''))
    .map((t) => ({ name: stripTags(t.title), amount: otAmount(t), sort: typeof t.sortOrder === 'number' ? t.sortOrder : 0 }))
    .filter((m): m is { name: string; amount: number; sort: number } => m.amount !== null && m.amount !== 0)
    .sort((a, b) => a.sort - b.sort);
  const signedSum = modifiers.reduce((s, m) => s + m.amount, 0);

  // Bestellsumme: ot_subtotal bevorzugt (auch vor dem Laden der Positionen verfügbar), sonst aus Positionen.
  const otSub = byClass('ot_subtotal');
  const subtotal = (otSub ? otAmount(otSub) : null) ?? computeSubtotal(items);

  // Gesamtpreis: ot_total bevorzugt, sonst totalSum.
  const otTotal = byClass('ot_total');
  const total = (otTotal ? otAmount(otTotal) : null) ?? parseMoney(totalSumStr);

  // Versand: ot_shipping bevorzugt; sonst Residuum (signierte Modifier rausrechnen: Rabatte + Zuschläge).
  const otShip = byClass('ot_shipping');
  let shipping = otShip ? otAmount(otShip) : null;
  if (shipping === null && total !== null && subtotal !== null) {
    shipping = Math.round((total - subtotal - signedSum) * 100) / 100;
  }

  const grand = total !== null ? formatPrice(total, currency) : escapeHtml(totalSumStr ?? '—');
  const modHtml = modifiers
    .map((m) => {
      const label = m.name || (m.amount < 0 ? 'Rabatt' : 'Zuschlag');
      return `<div class="totrow"><span>${escapeHtml(label)}</span><span>${escapeHtml(formatPrice(m.amount, currency))}</span></div>`;
    })
    .join('');
  return `
    <div class="totrow"><span>Bestellsumme</span><span>${escapeHtml(formatPrice(subtotal, currency))}</span></div>
    ${modHtml}
    <div class="totrow"><span>Versandkosten</span><span>${shipping !== null ? escapeHtml(formatPrice(shipping, currency)) : '—'}</span></div>
    <div class="totrow sum"><span>Gesamtpreis</span><span>${grand}</span></div>
  `;
}

/**
 * Rendert die Gambio-Attribute einer Position (Optionen wie Garantieverlängerung + Varianten-
 * werte wie Farbe) als kleine Unterzeilen unter dem Artikelnamen. Bei `priceType` '+'/'-' wird
 * der Auf-/Abschlag mit Vorzeichen gezeigt (Varianten haben '' → kein Preisbit).
 */
function renderItemAttributes(attrs: GambioItemAttribute[] | null | undefined, taxRate: number | null, currency: string = 'EUR'): string {
  if (!Array.isArray(attrs) || attrs.length === 0) return '';
  const lines = attrs
    .map((a) => {
      const name = (a.name ?? '').trim();
      const value = (a.value ?? '').trim();
      if (!name && !value) return '';
      const label = name && value
        ? `${escapeHtml(name)}: ${escapeHtml(value)}`
        : escapeHtml(name || value);
      let priceBit = '';
      if ((a.priceType === '+' || a.priceType === '-') && typeof a.price === 'number' && a.price > 0) {
        const sign = a.priceType === '-' ? '−' : '+';
        // Gambio liefert den Attribut-Aufpreis NETTO → brutto rechnen, damit er zu den (brutto
        // angezeigten) Positionspreisen passt. Ohne Steuersatz bleibt netto = brutto.
        const gross = taxRate ? Math.round(a.price * (1 + taxRate / 100) * 100) / 100 : a.price;
        priceBit = ` <span style="color:var(--muted);">(${sign}${escapeHtml(formatPrice(gross, currency))})</span>`;
      }
      return `<div style="color:var(--muted);font-size:11px;margin-top:0.15em;">↳ ${label}${priceBit}</div>`;
    })
    .filter(Boolean)
    .join('');
  return lines;
}

/** Art.-Nr.-Zelle: als Deeplink zum Vario-Artikel, wenn ein Mapping (sku → vario_article_id) existiert. */
function renderSkuCell(sku: string | null, articleIdBySku: Map<string, string>): string {
  if (!sku) return '<span style="color:var(--muted);">—</span>';
  const url = sku ? varioArticleUrl(articleIdBySku.get(sku)) : null;
  return url
    ? `<a href="${escapeHtml(url)}" target="_blank" rel="noopener" class="vart" title="Artikel in Vario öffnen">${escapeHtml(sku)}</a>`
    : `<span class="vart" style="border-bottom:none;cursor:default;">${escapeHtml(sku)}</span>`;
}

function renderPositionsTable(items: OrderItemRow[], articleIdBySku: Map<string, string>, currency: string = 'EUR'): string {
  // Gambio-Item (live verifiziert 2026-06-02): `price` = Einzelpreis, `finalPrice` =
  // Zeilensumme (price × quantity). Beide sind Euro-Dezimalzahlen (KEINE Cents).
  const rows = items
    .map((it) => {
      const qty = typeof it.payload?.quantity === 'number' ? it.payload.quantity : null;
      const unit = typeof it.payload?.price === 'number' ? it.payload.price : null;
      const line =
        typeof it.payload?.finalPrice === 'number'
          ? it.payload.finalPrice
          : unit !== null && qty !== null
            ? unit * qty
            : null;
      const tax = typeof it.payload?.tax === 'number' ? it.payload.tax : null;
      return `
        <tr>
          <td><span class="art">${escapeHtml(it.title ?? '—')}</span>${renderItemAttributes(it.payload?.attributes, tax, currency)}</td>
          <td>${renderSkuCell(it.sku, articleIdBySku)}</td>
          <td class="r">${qty ?? '—'}</td>
          <td class="r">${escapeHtml(formatPrice(unit, currency))}</td>
          <td class="r">${escapeHtml(formatTax(tax))}</td>
          <td class="r" style="font-weight:600;color:var(--ink);">${escapeHtml(formatPrice(line, currency))}</td>
        </tr>
      `;
    })
    .join('');
  return `
    <table class="postable">
      <thead>
        <tr>
          <th>Artikel</th>
          <th>Art.-Nr.</th>
          <th class="r">Menge</th>
          <th class="r">Einzelpreis</th>
          <th class="r">MwSt.</th>
          <th class="r">Gesamt</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

/** Gambio-Item `tax` ist der MwSt.-Satz in Prozent (z. B. 19). */
function formatTax(v: number | null): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `${v.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;
}

/** Gambio-Item-Preise sind Dezimalzahlen (keine Cents) → de-DE + Währungssymbol per ISO-Code. */
function formatPrice(v: number | null, currency: string): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const cur = (currency || 'EUR').trim().toUpperCase();
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: cur }).format(v);
  } catch {
    // Ungültiger/unbekannter ISO-Code → Fallback: Betrag + Code
    return `${v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
  }
}

function renderAddress(addr: GambioAddress | null, email: string | null): string {
  if (!addr) return `<div class="addr" style="color:var(--muted);font-style:italic;">Keine Adressdaten.</div>`;
  const name = [addr.firstName, addr.lastName].filter((s): s is string => !!s && s.trim().length > 0).join(' ').trim() || '—';
  const company = addr.company && addr.company.trim().length > 0 ? addr.company : '';
  const street = [addr.street, addr.houseNumber].filter((s): s is string => !!s && s.trim().length > 0).join(' ').trim() || '—';
  const cityLine = [addr.postcode, addr.city].filter((s): s is string => !!s && s.trim().length > 0).join(' ').trim() || '—';
  const country = addr.country && addr.country.trim().length > 0 ? addr.country : '';
  const addon = addr.additionalAddressInfo && addr.additionalAddressInfo.trim().length > 0 ? addr.additionalAddressInfo : '';
  return `
    <div class="addr">
      <div class="nm">${escapeHtml(name)}</div>
      ${company ? `<div class="co">${escapeHtml(company)}</div>` : ''}
      <div>${escapeHtml(street)}</div>
      ${addon ? `<div class="co">${escapeHtml(addon)}</div>` : ''}
      <div>${escapeHtml(cityLine)}${country ? ' · ' + escapeHtml(country) : ''}</div>
      ${email ? `<div class="mail">${escapeHtml(email)}</div>` : ''}
    </div>
  `;
}

/** Adressgleichheit (Liefer- vs. Rechnungsadresse) - kürzt die Rechnungs-Karte ab. */
function addressesEqual(a: GambioAddress | null, b: GambioAddress | null): boolean {
  if (!a || !b) return false;
  const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();
  return (
    norm(a.firstName) === norm(b.firstName) &&
    norm(a.lastName) === norm(b.lastName) &&
    norm(a.company) === norm(b.company) &&
    norm(a.street) === norm(b.street) &&
    norm(a.houseNumber) === norm(b.houseNumber) &&
    norm(a.postcode) === norm(b.postcode) &&
    norm(a.city) === norm(b.city) &&
    norm(a.country) === norm(b.country) &&
    norm(a.additionalAddressInfo) === norm(b.additionalAddressInfo)
  );
}

/** Gambio-Datum "YYYY-MM-DD HH:MM:SS" → "DD.MM.YYYY HH:MM". Fällt auf den Rohwert zurück. */
function formatDate(s: string | null): string {
  if (!s) return '—';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return s;
  const [, y, mo, d, h, mi] = m;
  return `${d}.${mo}.${y} ${h}:${mi}`;
}

/** Kurzform "DD.MM.YY" für die schmale Grid-Spalte. */
function formatDateShort(s: string | null): string {
  if (!s) return '—';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  const [, y, mo, d] = m;
  if (!y || !mo || !d) return s;
  return `${d}.${mo}.${y.slice(2)}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
